import { setAutoFreeze } from 'immer';
import { omit, partition } from 'ramda';
import type { Editor, Path } from 'slate';

import { assertNeverThrow, sumOf } from '../../util';
import type {
  AlineContentBlock,
  CMPageHeadersAndFooters,
  HeaderFooterProps,
  PageDimensions,
  RenderModelContentBlock,
  RenderModelDocument,
  RenderModelDynamicBlock,
  RenderModelFooter,
  RenderModelFooters,
  RenderModelHeader,
  RenderModelHeaders,
  RenderModelPage,
  RenderModelParagraph,
  RenderModelTable,
  RenderModelTableCell,
  RenderModelTableRow,
  RenderModelTOC,
  RMDocProps,
} from '../internal';
import {
  BreakVariant,
  createDefaultText,
  getCMPageHeadersAndFooters,
  getOrCreateCachedRMNode,
  getOrCreateRMHeaderFooterForPage,
  initNewRMPage,
  isBlockInstructionFieldElement,
  isRenderModelContentBlock,
  modifyRMTable,
  pageContentHeightCache,
  pageType,
  Queries,
} from '../internal';

// ToDo: Immer auto-freezes objects it touches in dev, and in some functions below we mutate those objects.  So we turn this off.  That should be alright?
setAutoFreeze(false);

// listId to lastListPath for that listId.
export type ListPaths = Record<string, { lastListPath: Path }>;

export interface BuildPagesState {
  accumulatedContentHeight: number;
  /** The active header and footer properties for the document. */
  activeHeaderFooterProps: HeaderFooterProps;
  /** The active page dimensions for the document. */
  activePageDimensions: PageDimensions;
  /** Store content model headers and footers for building headers and footers for each page. */
  cmHeadersAndFooters: CMPageHeadersAndFooters;
  cMParentPath: Path;
  /** The editor instance. */
  editor: Editor;
  /** The render model document properties. */
  rMDocProps: RMDocProps;
  /** The render model footer for the page we are building */
  rMFooter: RenderModelFooter;
  /** The render model footers for the document. */
  rMFooters: RenderModelFooters;
  /** The render model header for the page we are building */
  rMHeader: RenderModelHeader;
  /** The render model headers for the document. */
  rMHeaders: RenderModelHeaders;
  /** The current page we are building. */
  rMPage: RenderModelPage;
  /** The array of pages that will form the main page content of the render model. */
  rMPages: RenderModelPage[];
}

function reduceCMNode(
  state: BuildPagesState,
  cMChildNode: AlineContentBlock,
  cMChildIndex: number
): BuildPagesState {
  const { accumulatedContentHeight, cMParentPath, editor, rMFooters, rMHeaders, rMPage, rMPages } =
    state;
  const containerWidthPx = Queries.activePageContentWidth(editor, 'px');
  const cMChildPath = [...cMParentPath, cMChildIndex];

  switch (cMChildNode.type) {
    case 'paragraph': {
      const rMPara = getOrCreateCachedRMNode(editor, {
        cMNodeEntry: [cMChildNode, cMChildPath],
        containerWidthPx,
        isInHeaderFooter: false,
        pageNum: rMPage.pageNum,
      });

      const paragraphFits =
        rMPage.maxContentHeightPx - (accumulatedContentHeight + rMPara.heightPx) >= 0;
      if (paragraphFits && !rMPara.paragraphHasBreaks) {
        rMPage.children.push(rMPara);

        state.accumulatedContentHeight = accumulatedContentHeight + rMPara.heightPx;
        return state;
      }

      const splitParagraphs = splitParagraph(state, rMPara, rMPage, accumulatedContentHeight);

      return splitParagraphs.reduce<BuildPagesState>(
        (state, { rootNodePos, splitRMNode }, index) => {
          const isLast = index === splitParagraphs.length - 1;
          const { rMFooter, rMHeader, rMPage } = state;
          if (rootNodePos === 0) {
            rMPage.children.push(splitRMNode);

            if (isLast && splitRMNode.paragraphHasBreaks) {
              if (rMPage.children.length > 0) {
                rMPages.push(rMPage);
                rMHeaders.children.push(rMHeader);
                rMFooters.children.push(rMFooter);
              }

              const newPage = initNewRMPage(rMPage, {
                shouldIncrementPageNum: rMPage.children.length > 0,
              });

              return getUpdatedBuildPagesStateForNewPage({
                accumulatedContentHeight: 0,
                newPage,
                state,
              });
            }

            state.accumulatedContentHeight = accumulatedContentHeight + splitRMNode.heightPx;
            return state;
          }

          if (rMPage.children.length > 0) {
            rMPages.push(rMPage);
            rMHeaders.children.push(rMHeader);
            rMFooters.children.push(rMFooter);
          }

          const newPage = initNewRMPage(rMPage, {
            shouldIncrementPageNum: rMPage.children.length > 0,
          });

          newPage.children.push(splitRMNode);

          return getUpdatedBuildPagesStateForNewPage({
            accumulatedContentHeight: splitRMNode.heightPx,
            newPage,
            state,
          });
        },
        state
      );
    }
    case 'table':
    case 'dynamic-block': {
      const rMBlock = getOrCreateCachedRMNode(editor, {
        cMNodeEntry: [cMChildNode, cMChildPath],
        containerWidthPx,
        isInHeaderFooter: false,
        pageNum: rMPage.pageNum,
      });

      const rMBlockFits =
        rMPage.maxContentHeightPx - (accumulatedContentHeight + rMBlock.heightPx) >= 0;
      if (rMBlockFits) {
        rMPage.children.push(rMBlock);

        state.accumulatedContentHeight = accumulatedContentHeight + rMBlock.heightPx;
        return state;
      }

      // rMBlock doesn't fit.  Split it and add new pages as necessary.
      const splitRMBlocks = splitNonParaRMBlock(state, rMBlock, rMPage, accumulatedContentHeight);

      return splitRMBlocks.reduce<BuildPagesState>(
        (state, { rootNodePos: pagePos, splitRMNode: splitRMBlock }) => {
          const { rMFooter, rMHeader, rMPage } = state;
          if (pagePos === 0) {
            rMPage.children.push(splitRMBlock);

            state.accumulatedContentHeight = accumulatedContentHeight + splitRMBlock.heightPx;
            return state;
          }

          // pagePos is greater than 1, meaning we need to create new pages for subsequent split blocks.
          if (rMPage.children.length > 0) {
            rMPages.push(rMPage);
            rMHeaders.children.push(rMHeader);
            rMFooters.children.push(rMFooter);
          }

          const newPage = initNewRMPage(rMPage, {
            shouldIncrementPageNum: rMPage.children.length > 0,
          });

          newPage.children.push(splitRMBlock);

          return getUpdatedBuildPagesStateForNewPage({
            accumulatedContentHeight: splitRMBlock.heightPx,
            newPage,
            state,
          });
        },
        state
      );
    }
    case 'instructionField': {
      const rMBlock = getOrCreateCachedRMNode(editor, {
        cMNodeEntry: [cMChildNode, cMChildPath],
        containerWidthPx,
        isInHeaderFooter: false,
        pageNum: rMPage.pageNum,
      });

      const rMBlockFits =
        rMPage.maxContentHeightPx - (accumulatedContentHeight + rMBlock.heightPx) >= 0;
      if (rMBlockFits) {
        rMPage.children.push(rMBlock);

        return {
          ...state,
          accumulatedContentHeight: accumulatedContentHeight + rMBlock.heightPx,
        };
      }

      // rMBlock doesn't fit.  Split it and add new pages as necessary.
      const splitRMBlocks = splitToc(state, rMBlock, rMPage, accumulatedContentHeight);

      return splitRMBlocks.reduce<BuildPagesState>(
        (state, { rootNodePos: pagePos, splitRMNode: splitRMBlock }) => {
          const { rMFooter, rMHeader, rMPage } = state;
          if (pagePos === 0) {
            rMPage.children.push(splitRMBlock);

            state.accumulatedContentHeight = accumulatedContentHeight + splitRMBlock.heightPx;
            return state;
          }

          // pagePos is greater than 1, meaning we need to create new pages for subsequent split blocks.
          if (rMPage.children.length > 0) {
            rMPages.push(rMPage);
            rMHeaders.children.push(rMHeader);
            rMFooters.children.push(rMFooter);
          }

          const newPage = initNewRMPage(rMPage, {
            shouldIncrementPageNum: rMPage.children.length > 0,
          });

          newPage.children.push(splitRMBlock);

          return getUpdatedBuildPagesStateForNewPage({
            accumulatedContentHeight: splitRMBlock.heightPx,
            newPage,
            state,
          });
        },
        state
      );
    }
    default:
      console.error('[buildRenderModel] Unexpected node:', cMChildNode);
      return state;
  }
}

/**
 * Returns the updated build pages state for a new page.
 * Creates new rMHeader, rMFooter, rMDocProps, and rMPage.
 * Updates rMPage with correct maxContentHeightPx.
 */
function getUpdatedBuildPagesStateForNewPage({
  accumulatedContentHeight,
  newPage,
  state,
}: {
  accumulatedContentHeight: number;
  newPage: RenderModelPage;
  state: BuildPagesState;
}): BuildPagesState {
  const { activeHeaderFooterProps, activePageDimensions, cmHeadersAndFooters, editor } = state;

  const { rMFooter, rMHeader } = getOrCreateRMHeaderFooterForPage(
    editor,
    cmHeadersAndFooters,
    activeHeaderFooterProps,
    newPage.pageNum
  );

  const rMDocProps = getUpdatedRMDocProps(state, rMHeader, rMFooter);

  const maxContentHeightPx = Queries.activePageContentHeight(
    {
      activePageDimensions,
      pageNumber: newPage.pageNum,
      rMDocProps,
    },
    'px'
  );

  return {
    ...state,
    accumulatedContentHeight,
    rMDocProps,
    rMFooter,
    rMHeader,
    rMPage: {
      ...newPage,
      maxContentHeightPx,
    },
  };
}

function getUpdatedRMDocProps(
  state: BuildPagesState,
  newHeader: RenderModelHeader,
  newFooter: RenderModelFooter
) {
  const { rMDocProps, rMFooters, rMHeaders } = state;

  return {
    ...rMDocProps,
    footers: {
      ...rMFooters,
      children: [...rMFooters.children, newFooter],
    },
    headers: {
      ...rMHeaders,
      children: [...rMHeaders.children, newHeader],
    },
  };
}

/**
 * Utility function for splitting a non-para rMBlock that doesn't fit on a page.
 */
function splitNonParaRMBlock(
  state: BuildPagesState,
  rMBlock: RenderModelTable | RenderModelDynamicBlock | RenderModelTOC,
  rMPage: RenderModelPage,
  accumulatedContentHeight: number
): SplitNodeDetails[] {
  switch (rMBlock.type) {
    case 'table':
      return splitTable(state, rMBlock, rMPage, accumulatedContentHeight);
    case 'dynamic-block':
      return splitDynamicBlock(state, rMBlock, rMPage, accumulatedContentHeight);
    case 'instructionField':
      if (isBlockInstructionFieldElement(rMBlock)) {
        return splitToc(state, rMBlock, rMPage, accumulatedContentHeight);
      }
      throw new Error(`Unexpected instruction field instruction: ${rMBlock.instruction}`);
    default:
      assertNeverThrow(rMBlock);
  }
}

interface SplitDynamicBlockDetails {
  rootNodePos: number;
  splitRMNode: RenderModelDynamicBlock;
}

interface DynamicAcc {
  accumulatedContentHeight: number;
  splitDynamicBlocks: SplitDynamicBlockDetails[];
}

function splitDynamicBlock(
  state: BuildPagesState,
  rMDynamicBlock: RenderModelDynamicBlock,
  rMPage: RenderModelPage,
  accumulatedContentHeight: number
): SplitDynamicBlockDetails[] {
  const { splitDynamicBlocks } = rMDynamicBlock.children.reduce<DynamicAcc>(
    (acc, child) => {
      const { accumulatedContentHeight, splitDynamicBlocks } = acc;
      const lastIndex = splitDynamicBlocks.length ? splitDynamicBlocks.length - 1 : 0;
      const {
        rootNodePos = 0,
        splitRMNode: splitRMDynamicBlock = { ...rMDynamicBlock, children: [], heightPx: 0 },
      } = splitDynamicBlocks[lastIndex] ?? {};
      const pageWithCorrectNum = { ...rMPage, pageNum: rMPage.pageNum + rootNodePos };
      const maxAvailablePageContentHeight = getMaxAvailablePageContentHeightPx(
        state,
        pageWithCorrectNum.pageNum
      );
      const childFits =
        maxAvailablePageContentHeight - (accumulatedContentHeight + child.heightPx) >= 0;
      if (childFits) {
        splitRMDynamicBlock.children.push(child);
        splitRMDynamicBlock.heightPx = splitRMDynamicBlock.heightPx + child.heightPx;

        return {
          accumulatedContentHeight: accumulatedContentHeight + child.heightPx,
          splitDynamicBlocks: !splitDynamicBlocks.length
            ? [{ rootNodePos, splitRMNode: splitRMDynamicBlock }]
            : splitDynamicBlocks,
        };
      }

      const splitChildren = splitRMBlock(
        state,
        child,
        {
          ...pageWithCorrectNum,
          maxContentHeightPx: maxAvailablePageContentHeight,
        },
        accumulatedContentHeight
      );

      return splitChildren.reduce<DynamicAcc>(
        (childAcc, { rootNodePos: dynamicBlockPos, splitRMNode }) => {
          const { splitDynamicBlocks } = childAcc;
          if (dynamicBlockPos === 0) {
            splitRMDynamicBlock.children.push(splitRMNode);
            splitRMDynamicBlock.heightPx = splitRMDynamicBlock.heightPx + splitRMNode.heightPx;

            return {
              accumulatedContentHeight: accumulatedContentHeight + splitRMNode.heightPx,
              splitDynamicBlocks: !splitDynamicBlocks.length
                ? [{ rootNodePos, splitRMNode: splitRMDynamicBlock }]
                : splitDynamicBlocks,
            };
          }

          childAcc.accumulatedContentHeight = splitRMNode.heightPx;
          childAcc.splitDynamicBlocks.push({
            rootNodePos: rootNodePos + dynamicBlockPos,
            splitRMNode: {
              ...rMDynamicBlock,
              children: [splitRMNode],
              heightPx: splitRMNode.heightPx,
            },
          });
          return childAcc;
        },
        acc
      );
    },
    { accumulatedContentHeight, splitDynamicBlocks: [] }
  );

  return splitDynamicBlocks;
}

interface SplitTocDetails {
  rootNodePos: number;
  splitRMNode: RenderModelTOC;
}

interface TocAcc {
  accumulatedContentHeight: number;
  splitTocs: SplitTocDetails[];
}

function splitToc(
  state: BuildPagesState,
  rMToc: RenderModelTOC,
  rMPage: RenderModelPage,
  accumulatedContentHeight: number
): SplitTocDetails[] {
  const { splitTocs } = rMToc.children.reduce<TocAcc>(
    (acc, child) => {
      const { accumulatedContentHeight, splitTocs } = acc;
      const lastIndex = splitTocs.length ? splitTocs.length - 1 : 0;
      const { rootNodePos = 0, splitRMNode: splitRMToc = { ...rMToc, children: [], heightPx: 0 } } =
        splitTocs[lastIndex] ?? {};
      const pageWithCorrectNum = { ...rMPage, pageNum: rMPage.pageNum + rootNodePos };
      const maxAvailablePageContentHeight = getMaxAvailablePageContentHeightPx(
        state,
        pageWithCorrectNum.pageNum
      );
      const childFits =
        maxAvailablePageContentHeight - (accumulatedContentHeight + child.heightPx) >= 0;
      if (childFits) {
        splitRMToc.children.push(child);
        splitRMToc.heightPx = splitRMToc.heightPx + child.heightPx;

        return {
          accumulatedContentHeight: accumulatedContentHeight + child.heightPx,
          splitTocs: !splitTocs.length ? [{ rootNodePos, splitRMNode: splitRMToc }] : splitTocs,
        };
      }

      const splitChildren = splitRMBlock(
        state,
        child,
        {
          ...pageWithCorrectNum,
          maxContentHeightPx: maxAvailablePageContentHeight,
        },
        accumulatedContentHeight
      );

      return splitChildren.reduce<TocAcc>((childAcc, { rootNodePos: tocPos, splitRMNode }) => {
        const { splitTocs } = childAcc;
        if (tocPos === 0) {
          splitRMToc.children.push(splitRMNode);
          splitRMToc.heightPx = splitRMToc.heightPx + splitRMNode.heightPx;

          return {
            accumulatedContentHeight: accumulatedContentHeight + splitRMNode.heightPx,
            splitTocs: !splitTocs.length ? [{ rootNodePos, splitRMNode: splitRMToc }] : splitTocs,
          };
        }

        childAcc.accumulatedContentHeight = splitRMNode.heightPx;
        childAcc.splitTocs = [
          ...splitTocs,
          {
            rootNodePos: rootNodePos + tocPos,
            splitRMNode: {
              ...rMToc,
              children: [splitRMNode],
              heightPx: splitRMNode.heightPx,
            },
          },
        ];
        return childAcc;
      }, acc);
    },
    { accumulatedContentHeight, splitTocs: [] }
  );

  return splitTocs;
}

interface SplitParagraphDetails {
  rootNodePos: number;
  splitRMNode: RenderModelParagraph;
}

function splitParagraph(
  state: BuildPagesState,
  rMPara: RenderModelParagraph,
  rMPage: RenderModelPage,
  accumulatedContentHeight: number
): SplitParagraphDetails[] {
  const spacingAfterPx = Queries.renderedParagraphSpacingAfter(rMPara, 'px');
  const spacingBeforePx = Queries.renderedSpacingBeforePx(rMPara);
  const { borderLayoutBottomPx, borderLayoutTopPx } = rMPara;
  const { splitParagraphs } = rMPara.children.reduce<{
    accumulatedContentHeight: number;
    splitParagraphs: SplitParagraphDetails[];
  }>(
    (acc, line, index) => {
      const isFirstLineOfParagraph = index === 0;
      const splitIndex = rMPara.splitIndex + index;
      const { accumulatedContentHeight, splitParagraphs } = acc;
      const isLastLine = index === rMPara.children.length - 1;
      const lastIndex = splitParagraphs.length ? splitParagraphs.length - 1 : 0;
      const {
        rootNodePos = 0,
        // We're unsure if the splitRMPara contains a line w/ a content break, so init to undefined.
        splitRMNode: splitRMPara = {
          ...rMPara,
          borderLayoutBottomPx: 0,
          borderLayoutTopPx: 0,
          children: [],
          heightPx: 0,
          paragraphHasBreaks: undefined,
          splitIndex,
        },
      } = splitParagraphs[lastIndex] ?? {};
      const adjustedLineHeight = Queries.adjustedLineHeight(line, spacingAfterPx, spacingBeforePx);
      const topInsetForLine = isFirstLineOfParagraph ? borderLayoutTopPx : 0;
      const bottomInsetForLine = isLastLine ? borderLayoutBottomPx : 0;
      const lineHeightWithBorderInsets = adjustedLineHeight + topInsetForLine + bottomInsetForLine;
      const paragraphHasBreaks = line.contentBreak
        ? line.contentBreak.variant === BreakVariant.Page
        : splitRMPara.paragraphHasBreaks;
      const prevLine = index > 0 ? rMPara.children[index - 1] : null;
      const pageWithCorrectNum = { ...rMPage, pageNum: rMPage.pageNum + rootNodePos };
      const maxAvailablePageContentHeight = getMaxAvailablePageContentHeightPx(
        state,
        pageWithCorrectNum.pageNum
      );
      const lineFits =
        maxAvailablePageContentHeight - (accumulatedContentHeight + lineHeightWithBorderInsets) >=
        0;
      if (lineFits && !prevLine?.contentBreak) {
        const fragmentTopInset = splitRMPara.children.length === 0 ? topInsetForLine : 0;
        splitRMPara.children.push(line);
        splitRMPara.borderLayoutTopPx = fragmentTopInset
          ? borderLayoutTopPx
          : splitRMPara.borderLayoutTopPx;
        splitRMPara.borderLayoutBottomPx = isLastLine ? borderLayoutBottomPx : 0;
        splitRMPara.heightPx = splitRMPara.heightPx + lineHeightWithBorderInsets;
        splitRMPara.paragraphHasBreaks = paragraphHasBreaks;

        return {
          accumulatedContentHeight: accumulatedContentHeight + lineHeightWithBorderInsets,
          splitParagraphs: !splitParagraphs.length
            ? [
                {
                  rootNodePos,
                  splitRMNode: {
                    ...splitRMPara,
                    borderLayoutBottomPx: isLastLine ? borderLayoutBottomPx : 0,
                    borderLayoutTopPx: borderLayoutTopPx,
                    splitIndex,
                  } satisfies RenderModelParagraph,
                },
              ]
            : splitParagraphs,
        };
      }

      return {
        accumulatedContentHeight: lineHeightWithBorderInsets,
        splitParagraphs: [
          ...splitParagraphs,
          {
            rootNodePos: rootNodePos + 1,
            splitRMNode: {
              ...splitRMPara,
              borderLayoutBottomPx: isLastLine ? borderLayoutBottomPx : 0,
              borderLayoutTopPx: isFirstLineOfParagraph ? borderLayoutTopPx : 0,
              children: [line],
              heightPx: lineHeightWithBorderInsets,
              paragraphHasBreaks,
              splitIndex,
            } satisfies RenderModelParagraph,
          },
        ],
      };
    },
    { accumulatedContentHeight, splitParagraphs: [] }
  );

  return splitParagraphs.map(({ rootNodePos, splitRMNode }) => {
    const isFirstFragment = Boolean(splitRMNode.children.at(0)?.isFirstLine);
    const isLastFragment = Boolean(splitRMNode.children.at(-1)?.isLastLine);
    const bordersToDraw = splitRMNode.bordersToDraw ? { ...splitRMNode.bordersToDraw } : undefined;

    /**
     * Word leaves paragraph borders open where one paragraph continues across
     * a page or column split. Table borders intentionally use the opposite
     * behavior and are replicated at each split edge.
     */
    if (bordersToDraw && !isFirstFragment) delete bordersToDraw.top;
    if (bordersToDraw && !isLastFragment) delete bordersToDraw.bottom;

    return {
      rootNodePos,
      splitRMNode: {
        ...splitRMNode,
        borderMergesWithNext: splitRMNode.borderMergesWithNext || !isLastFragment,
        borderMergesWithPrev: splitRMNode.borderMergesWithPrev || !isFirstFragment,
        bordersToDraw,
      },
    };
  });
}

interface SplitTableDetails {
  rootNodePos: number;
  splitRMNode: RenderModelTable;
}

function splitTable(
  state: BuildPagesState,
  rMTable: RenderModelTable,
  rMPage: RenderModelPage,
  initialPageAccumulatedContentHeight: number
): SplitTableDetails[] {
  const nextRMPage: RenderModelPage = { ...rMPage, pageNum: rMPage.pageNum + 1 };
  const maxAvailablePageContentHeight = getMaxAvailablePageContentHeightPx(state, rMPage.pageNum);

  const tableTopBorder = Queries.getTableMaximumBorderWidth(rMTable, 'top', 'px');
  const initialAccumulatedContentHeight = initialPageAccumulatedContentHeight + tableTopBorder / 2;

  /**
   * The point at which to split the table (if it needs splitting) relative to the top of the first
   * table row.
   */
  const splitY = maxAvailablePageContentHeight - initialAccumulatedContentHeight;

  if (rMTable.heightPx <= splitY) return [{ rootNodePos: 0, splitRMNode: rMTable }];

  const { lastRowIndex, midRowSplit } = getTableSplitLocation(state, rMTable, rMPage, {
    initialAccumulatedContentHeight,
    splitY,
  });

  // If no rows fit on the current page, skip to the next page and continue splitting
  if (lastRowIndex === -1) {
    // Avoid infinite recursion if we're already on a blank page
    if (initialPageAccumulatedContentHeight === 0)
      return [{ rootNodePos: 0, splitRMNode: rMTable }];

    return splitTable(state, rMTable, nextRMPage, 0).map(splitTable => ({
      ...splitTable,
      rootNodePos: splitTable.rootNodePos + 1,
    }));
  }

  const entirelyFittingRows = rMTable.children.slice(0, lastRowIndex + (midRowSplit ? 0 : 1));
  const entirelyNonFittingRows = rMTable.children.slice(lastRowIndex + 1);
  const bisectedRow = midRowSplit ? (rMTable.children.at(lastRowIndex) ?? null) : null;

  // Shallowly clone each row and its children array so that we can mutate them safely
  const rowsAboveSplit = entirelyFittingRows.map(rMTableRow => ({
    ...rMTableRow,
    children: rMTableRow.children.slice(),
  }));

  const rowsBelowSplit = entirelyNonFittingRows.map(rMTableRow => ({
    ...rMTableRow,
    children: rMTableRow.children.slice(),
  }));

  if (bisectedRow) {
    const topOfBisectedRow = sumOf(entirelyFittingRows, 'heightPx');

    const splitCells = bisectedRow.children.map(rMTableCell =>
      splitTableCell(state, rMTable, rMTableCell, rMPage, {
        initialAccumulatedContentHeight: initialAccumulatedContentHeight + topOfBisectedRow,
      })
    );

    rowsAboveSplit.push({
      ...bisectedRow,
      children: splitCells.map(([cellAboveSplit]) => cellAboveSplit),
    });

    rowsBelowSplit.unshift({
      ...bisectedRow,
      children: splitCells.map(([, cellBelowSplit]) => cellBelowSplit),
    });
  }

  /**
   * Ensure that all cells in the last row above the split draw their bottom borders, and all cells
   * in the first row below the split draw their top borders.
   */
  function enableBorderDrawingInDirection(
    rMTableRow: RenderModelTableRow,
    direction: 'top' | 'bottom'
  ) {
    rMTableRow.children = rMTableRow.children.map(rMTableCell => ({
      ...rMTableCell,
      renderedBorders: {
        ...rMTableCell.renderedBorders,
        [direction]: rMTableCell.renderedBorders[direction].map(border => ({
          ...border,
          draw: true,
        })),
      },
    }));
  }

  const lastRowAboveSplit = rowsAboveSplit.at(-1);
  if (lastRowAboveSplit) enableBorderDrawingInDirection(lastRowAboveSplit, 'bottom');

  const firstRowBelowSplit = rowsBelowSplit.at(0);
  if (firstRowBelowSplit) {
    enableBorderDrawingInDirection(firstRowBelowSplit, 'top');

    /**
     * Check if any vertically merged cells have been split and handle them if so. This is
     * indicated by the presence of a non-first cell in the first row below the split.
     */
    firstRowBelowSplit.children.forEach((cellBelowSplit, cellBelowSplitIndex) => {
      if (cellBelowSplit.verticalMergeIsStart) return;

      let firstVMCellIndex = -1;

      /**
       * Scan the rows above the split in reverse order, looking for the first cell in the same
       * column where verticalMergeIsStart is true.
       */
      const firstVMCellRowIndex = rowsAboveSplit.findLastIndex(rMTableRow => {
        for (let cellIndex = 0; cellIndex < rMTableRow.children.length; cellIndex++) {
          const rMTableCell = rMTableRow.children[cellIndex];

          if (
            rMTableCell.startColumnIndex === cellBelowSplit.startColumnIndex &&
            rMTableCell.verticalMergeIsStart
          ) {
            firstVMCellIndex = cellIndex;
            return true;
          }
        }

        return false;
      });

      // Skip processing if the first cell cannot be found, possibly due to a malformed table
      if (firstVMCellRowIndex === -1 || firstVMCellIndex === -1) return;

      const firstVMCellRow = rowsAboveSplit.at(firstVMCellRowIndex);
      const firstVMCell = firstVMCellRow?.children.at(firstVMCellIndex);

      const lastVMCellAboveSplit = lastRowAboveSplit?.children.find(
        rMTableCell => rMTableCell.startColumnIndex === cellBelowSplit.startColumnIndex
      );

      if (!firstVMCellRow || !firstVMCell || !lastVMCellAboveSplit) return;

      /**
       * Get the top of the first cell relative to the top of the first table row. This requires
       * knowing the heights of all rows prior to the one this first cell occupies. Even though no
       * heights have yet been recomputed using modifyRMTable, these heights are guaranteed to be
       * correct since the only row above the split with an out-of-date height value is the bisected
       * row (if any).
       */
      const firstVMCellTop = sumOf(rowsAboveSplit.slice(0, firstVMCellRowIndex), 'heightPx');

      /**
       * The bottom margin of the last vertically merged cell above the split now becomes the bottom
       * margin of the resultant vertically merged cell. Pass this bottom margin to splitTableCell
       * so that we take it into consideration when splitting the vertically merged cells content.
       */
      const marginBottom = Queries.renderedTableCellMargin(
        rMTable,
        lastVMCellAboveSplit,
        'bottom',
        'px'
      );

      const [{ children: contentAboveSplit }, { children: contentBelowSplit }] = splitTableCell(
        state,
        rMTable,
        firstVMCell,
        rMPage,
        {
          initialAccumulatedContentHeight: initialAccumulatedContentHeight + firstVMCellTop,
          overrideMarginBottom: marginBottom,
        }
      );

      firstVMCellRow.children[firstVMCellIndex] = {
        ...firstVMCell,
        children: contentAboveSplit,
      };

      firstRowBelowSplit.children[cellBelowSplitIndex] = {
        ...cellBelowSplit,
        children: contentBelowSplit,
      };
    });
  }

  const tableAboveSplit = modifyRMTable(rMTable, { children: rowsAboveSplit });
  const tableBelowSplit = modifyRMTable(rMTable, { children: rowsBelowSplit });

  // Recursively continue splitting
  return [
    {
      rootNodePos: 0,
      splitRMNode: tableAboveSplit,
    },
    ...splitTable(state, tableBelowSplit, nextRMPage, 0).map(({ rootNodePos, splitRMNode }) => ({
      rootNodePos: rootNodePos + 1,
      splitRMNode,
    })),
  ];
}

/**
 * Determine the row at which the given table should be split.
 */
function getTableSplitLocation(
  state: BuildPagesState,
  rMTable: RenderModelTable,
  rMPage: RenderModelPage,
  options: { initialAccumulatedContentHeight: number; splitY: number }
): {
  /** The index of the last row that partly or wholly fits above the split. */
  lastRowIndex: number;
  /** Whether the row at lastRowIndex should be split. */
  midRowSplit: boolean;
} {
  const { initialAccumulatedContentHeight, splitY } = options;

  let totalFittingRowHeights = 0;
  let totalFittingRowHeightsIncludingBottomBorder = 0;

  // Find the last row that partly or wholly fits above the split, including its full bottom border
  let lastRowIndex = rMTable.children.findIndex(rMTableRow => {
    const maxBottomBorderWidth = Queries.getRMTableRowMaximumBorderWidth(
      rMTableRow,
      'bottom',
      'px'
    );
    const totalRowHeight = totalFittingRowHeights + rMTableRow.heightPx;
    const totalRowHeightIncludingBottomBorder = totalRowHeight + maxBottomBorderWidth / 2;

    const fits = totalRowHeightIncludingBottomBorder <= splitY;
    const exactlyFits = totalRowHeightIncludingBottomBorder === splitY;

    if (fits) {
      totalFittingRowHeights = totalRowHeight;
      totalFittingRowHeightsIncludingBottomBorder = totalRowHeightIncludingBottomBorder;
    }

    return !fits || exactlyFits;
  });

  // If all rows fit, the last row is the last row
  if (lastRowIndex === -1) lastRowIndex = rMTable.children.length - 1;

  const midRowSplit = totalFittingRowHeightsIncludingBottomBorder !== splitY;
  const bisectedRow = midRowSplit ? (rMTable.children.at(lastRowIndex) ?? null) : null;

  if (bisectedRow) {
    /**
     * Some rows cannot be split, either because splitting is disabled using the cantSplit property
     * or because splitting one or more cells in this row would leave the upper split cell empty.
     */
    const bisectedRowIsSplittable =
      !bisectedRow.cantSplit &&
      bisectedRow.children.every(rMTableCell =>
        getFirstCellContentFits(state, rMTable, rMTableCell, {
          accumulatedContentHeight: initialAccumulatedContentHeight + totalFittingRowHeights,
          rMPage,
        })
      );

    // If the row cannot be split, split at the top of the row instead
    if (!bisectedRowIsSplittable)
      return {
        lastRowIndex: lastRowIndex - 1,
        midRowSplit: false,
      };
  }

  return { lastRowIndex, midRowSplit };
}

/**
 * Check if any content of the given cell fits on the current page.
 */
function getFirstCellContentFits(
  state: BuildPagesState,
  rMTable: RenderModelTable,
  rMTableCell: RenderModelTableCell,
  options: {
    accumulatedContentHeight: number;
    rMPage: RenderModelPage;
  }
): boolean {
  // If verticalMergeIsStart is false, the cell will not be rendered so its contents are irrelevant
  if (!rMTableCell.verticalMergeIsStart) return true;

  const { accumulatedContentHeight, rMPage } = options;
  const maxAvailablePageContentHeight = getMaxAvailablePageContentHeightPx(state, rMPage.pageNum);

  const topMargin = Queries.renderedTableCellMargin(rMTable, rMTableCell, 'top', 'px');
  const bottomMargin = Queries.renderedTableCellMargin(rMTable, rMTableCell, 'bottom', 'px');
  const verticalMargin = topMargin + bottomMargin;

  const maxBottomBorderWidth = Queries.getRMTableCellMaximumBorderWidth(
    rMTableCell,
    'bottom',
    'px'
  );

  const firstChild = rMTableCell.children.find(isRenderModelContentBlock);
  if (!firstChild) return true;

  const heightWithFirstChild = firstChild.heightPx + verticalMargin + maxBottomBorderWidth / 2;
  const availableHeight = maxAvailablePageContentHeight - accumulatedContentHeight;
  if (heightWithFirstChild <= availableHeight) return true;

  // Check if the child can be split to fit on the current page
  return splitRMBlock(
    state,
    firstChild,
    rMPage,
    accumulatedContentHeight + verticalMargin + maxBottomBorderWidth / 2
  ).some(({ rootNodePos }) => rootNodePos === 0);
}

function splitTableCell(
  state: BuildPagesState,
  rMTable: RenderModelTable,
  rMTableCell: RenderModelTableCell,
  rMPage: RenderModelPage,
  options: {
    initialAccumulatedContentHeight: number;
    overrideMarginBottom?: number;
  }
): [RenderModelTableCell, RenderModelTableCell] {
  const { initialAccumulatedContentHeight, overrideMarginBottom } = options;
  const { children: baseChildren, verticalMergeIsEnd, verticalMergeIsStart } = rMTableCell;
  const children = baseChildren.filter(isRenderModelContentBlock);
  const maxAvailablePageContentHeight = getMaxAvailablePageContentHeightPx(state, rMPage.pageNum);

  /**
   * The cell below the split has a verticalMergeIsStart property of true if and only if the
   * original cell's verticalMergeIsStart and verticalMergeIsEnd properties were both true (i.e. if
   * the original cell was not vertically merged). This ensures that if a true vertically merged
   * cell is split, we can detect this in splitTable and split its content using the special logic
   * required for vertically merged cells.
   */
  const verticalMergeIsStartBelowSplit = verticalMergeIsStart && verticalMergeIsEnd;

  const topMargin = Queries.renderedTableCellMargin(rMTable, rMTableCell, 'top', 'px');
  const bottomMargin =
    overrideMarginBottom ?? Queries.renderedTableCellMargin(rMTable, rMTableCell, 'bottom', 'px');
  const verticalMargin = topMargin + bottomMargin;

  const maxBottomBorderWidth = Queries.getRMTableCellMaximumBorderWidth(
    rMTableCell,
    'bottom',
    'px'
  );

  let accumulatedContentHeight =
    initialAccumulatedContentHeight + verticalMargin + maxBottomBorderWidth / 2;

  const firstNonFittingChildIndex = children.findIndex(({ heightPx }) => {
    if (accumulatedContentHeight + heightPx > maxAvailablePageContentHeight) return true;
    accumulatedContentHeight += heightPx;
    return false;
  });

  /**
   * If all children fit or verticalMergeIsStart is false, keep all content above the split. If
   * verticalMergeIsStart is false, then the cell will not be rendered and its content, margins and
   * borders are irrelevant to splitting.
   */
  const allChildrenFit = firstNonFittingChildIndex === -1;
  if (allChildrenFit || !verticalMergeIsStart) {
    return [
      rMTableCell,
      {
        ...rMTableCell,
        children: [{ ...createDefaultText(), isEmptySplitNodeText: true, text: '' }],
        verticalMergeIsStart: verticalMergeIsStartBelowSplit,
      },
    ];
  }

  const childrenAboveSplit = children.slice(0, firstNonFittingChildIndex);
  const childrenBelowSplit = children.slice(firstNonFittingChildIndex + 1);

  const childToSplit = children[firstNonFittingChildIndex];

  const splitChildren = splitRMBlock(
    state,
    childToSplit,
    { ...rMPage, maxContentHeightPx: maxAvailablePageContentHeight },
    accumulatedContentHeight
  );

  const [splitChildrenAboveSplit, splitChildrenBelowSplit] = partition(
    ({ rootNodePos }) => rootNodePos === 0,
    splitChildren
  );

  return [
    {
      ...rMTableCell,
      children: [
        ...childrenAboveSplit,
        ...splitChildrenAboveSplit.map(({ splitRMNode }) => splitRMNode),
      ],
    },

    {
      ...rMTableCell,
      children: [
        ...splitChildrenBelowSplit.map(({ splitRMNode }) => splitRMNode),
        ...childrenBelowSplit,
      ],
      verticalMergeIsStart: verticalMergeIsStartBelowSplit,
    },
  ];
}

type SplitNodeDetails =
  | SplitParagraphDetails
  | SplitTableDetails
  | SplitDynamicBlockDetails
  | SplitTocDetails;

/**
 * Split an rMBlock that doesn't fit.
 */
function splitRMBlock(
  state: BuildPagesState,
  rMBlockChild: RenderModelContentBlock,
  rMPage: RenderModelPage,
  accumulatedContentHeight: number
): SplitNodeDetails[] {
  switch (rMBlockChild.type) {
    case 'paragraph':
      return splitParagraph(state, rMBlockChild, rMPage, accumulatedContentHeight);
    case 'table':
    case 'dynamic-block':
    case 'instructionField':
      return splitNonParaRMBlock(state, rMBlockChild, rMPage, accumulatedContentHeight);
  }
}

/**
 * Calculates the maximum available content height for a specific page number
 * by creating temporary header/footer for that page and calculating content height
 */
function getMaxAvailablePageContentHeightPx(state: BuildPagesState, pageNum: number): number {
  if (pageContentHeightCache.has(pageNum)) {
    return pageContentHeightCache.get(pageNum)!;
  }

  const { activeHeaderFooterProps, activePageDimensions, cmHeadersAndFooters, editor, rMDocProps } =
    state;

  const { rMFooter, rMHeader } = getOrCreateRMHeaderFooterForPage(
    editor,
    cmHeadersAndFooters,
    activeHeaderFooterProps,
    pageNum
  );

  const newRMDocProps = {
    ...rMDocProps,
    footers: {
      ...rMDocProps.footers,
      children: [...rMDocProps.footers.children],
    },
    headers: {
      ...rMDocProps.headers,
      children: [...rMDocProps.headers.children],
    },
  };

  newRMDocProps.headers.children[pageNum - 1] = rMHeader;
  newRMDocProps.footers.children[pageNum - 1] = rMFooter;

  const contentHeight = Queries.activePageContentHeight(
    {
      activePageDimensions,
      pageNumber: pageNum,
      rMDocProps: newRMDocProps,
    },
    'px'
  );

  pageContentHeightCache.set(pageNum, contentHeight);

  return contentHeight;
}

export function buildRenderModel(editor: Editor): RenderModelDocument {
  pageContentHeightCache.clear();

  const initialPageNum = 1;
  const [cMDoc, cMDocPath] = Queries.getDocument(editor);
  const activePageDimensions = Queries.activePageDimensions(editor, 'in');
  const relativePath = Queries.getPathRelativeToParent(cMDocPath);

  const headersEntry = Queries.getHeadersElement(editor);
  const footersEntry = Queries.getFootersElement(editor);

  if (!headersEntry) throw new Error('Expected headers.');
  if (!footersEntry) throw new Error('Expected footers.');

  const cmHeadersAndFooters = getCMPageHeadersAndFooters(editor, headersEntry, footersEntry);

  const activeHeaderFooterProps = Queries.activeHeaderOrFooterProps(cMDoc);

  const [headersPart, headersPartPath] = headersEntry;
  const [footersPart, footersPartPath] = footersEntry;

  // We need to create header/footer to get the correct content height for building the first page.
  const { rMFooter: firstPageFooter, rMHeader: firstPageHeader } = getOrCreateRMHeaderFooterForPage(
    editor,
    cmHeadersAndFooters,
    activeHeaderFooterProps,
    initialPageNum
  );

  const rMDocProps: RMDocProps = {
    ...omit(['children'], cMDoc),
    footers: {
      ...footersPart,
      children: [firstPageFooter],
      relativePath: Queries.getPathRelativeToParent(footersPartPath),
    },
    headers: {
      ...headersPart,
      children: [firstPageHeader],
      relativePath: Queries.getPathRelativeToParent(headersPartPath),
    },
    relativePath,
  };

  const maxContentHeightPx = Queries.activePageContentHeight(
    {
      activePageDimensions,
      pageNumber: initialPageNum,
      rMDocProps,
    },
    'px'
  );

  const initialState: BuildPagesState = {
    accumulatedContentHeight: 0,
    activeHeaderFooterProps,
    activePageDimensions,
    cMParentPath: cMDocPath,
    cmHeadersAndFooters,
    editor,
    rMDocProps,
    rMFooter: firstPageFooter,
    rMFooters: {
      ...footersPart,
      children: [],
      relativePath: Queries.getPathRelativeToParent(footersPartPath),
    },
    rMHeader: firstPageHeader,
    rMHeaders: {
      ...headersPart,
      children: [],
      relativePath: Queries.getPathRelativeToParent(headersPartPath),
    },
    rMPage: {
      children: [],
      maxContentHeightPx,
      pageNum: initialPageNum,
      suggestedEdits: [],
      type: pageType,
    },
    rMPages: [],
  };
  const { rMFooter, rMFooters, rMHeader, rMHeaders, rMPage, rMPages } = cMDoc.children.reduce(
    reduceCMNode,
    initialState
  );
  if (rMPage.children.length > 0) {
    rMPages.push(rMPage);
    rMHeaders.children.push(rMHeader);
    rMFooters.children.push(rMFooter);
  }

  const document: RenderModelDocument = {
    ...cMDoc,
    children: rMPages,
    footers: rMFooters,
    headers: rMHeaders,
    relativePath,
  };
  editor.renderModel = document;

  return document;
}
